import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { STATUS } from "../../design/ui";
import { fmtInputDate, normalizeProject, statusKey } from "../../design/domain";
import { useApp } from "../../design/AppContext";
import { apiPutForm } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { jwtEncode } from "../../utils/jwt";
import { buildScheduleView, workdaysBetween, ymdDiff } from "./rdSummary";
import useGoalBufferDays from "./useGoalBufferDays";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Banner, RowButton, StatusChip } from "../../components/mui";
import { SkeletonRows } from "../../components/loading";
import {
  MdAdd,
  MdCheck,
  MdCircle,
  MdErrorOutline,
  MdRemove,
  MdWarningAmber,
} from "react-icons/md";

/**
 * 時程 — 排工項到里程碑（設計稿 #screen-goal-plan，el-pm-template_4.html L1786-1873）
 *
 * RD 主管的畫面：左邊是還沒掛到任何里程碑的工項，一張卡一件，卡上就能選里程碑與負責人；
 * 右邊是選定的里程碑與它底下已經排好的工項，以及最重要的那一句 ——「這個里程碑排得下嗎」。
 *
 * 為什麼底下是整句算式而不是甘特重疊圖：
 * 重疊圖看得出「有東西卡住了」，但看的人還要自己數格子才知道差幾天；
 * 而真正要做的決定只有三個（往前排、換人做、請訂定者改日期），
 * 那三個決定只需要一個數字：超出幾天。所以直接把句子寫出來。
 *
 * 零新端點：
 *   · 未排工項 ＝ GET /Schedule/WorkItems 裡 phase_id 為空者
 *   · 寫入     ＝ 既有的 PUT /Task 帶 PhaseId（pages/Project/Detail.js 已有先例）
 *
 * 版面：外層不再包一層頁面容器。/Schedule 開頭的路由命中 Layout 的
 * NO_PAGE_CAP，這裡只拿到內距、不吃 1120px 上限，也不自己寫 max-width。
 */

/* ===================== 常數 ===================== */

/**
 * 估時換算工作天用的每日工時。
 *
 * 這是一個寫在畫面上、講得出來的換算係數，不是後端資料 ——
 * 所以句子裡一定要把「以一天 8 小時換算」講出來，不能只丟一個 21 天。
 * 系統沒有任何「每個人每天實際做幾小時」的資料，硬編一個看起來更精準的數字
 * （例如 6.5 小時／天）只會讓人以為那是量出來的。
 */
const HOURS_PER_DAY = 8;

/** 左欄一次最多列幾張卡。排完就會從這份清單消失，下一批自然補上來 */
const UNPLANNED_MAX = 10;

/** 狀態 → StatusChip 的 kind（與 pages/MyTask/Index.js 的 STATUS_TAG 同一份規則，不要各寫一套） */
const STATUS_TAG = {
  pending: { kind: "closed", mark: <MdRemove /> },
  progress: { kind: "progress", mark: <MdCircle size="0.6em" /> },
  review: { kind: "warn", mark: <MdErrorOutline /> },
  done: { kind: "done", mark: <MdCheck /> },
  closed: { kind: "closed", mark: <MdRemove /> },
};

/** 等級 → StatusChip 的 kind（與 Goals.js 的 LEVEL_TAG 同一份） */
const LEVEL_TAG = {
  attention: "attention",
  warn: "warn",
  done: "done",
  closed: "closed",
  plan: "progress",
};

/* ===================== 取值與格式 ===================== */

/** 後端的 DateTimeOffset → 當地時區的 "YYYY-MM-DD"（理由同 Goals.js 的 ymdOf） */
function ymdOf(v) {
  if (!v) return "";
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
}

/** "2026-10-15" → 「10 月 15 日」；跨年才補年份 */
function longDate(ymd) {
  if (!ymd) return "";
  const d = dayjs(ymd);
  if (!d.isValid()) return "";
  return d.isSame(dayjs(), "year") ? d.format("M 月 D 日") : d.format("YYYY 年 M 月 D 日");
}

/** "2026-10-15" → "10/15" */
function shortDate(ymd) {
  if (!ymd) return "";
  const d = dayjs(ymd);
  return d.isValid() ? d.format("M/D") : "";
}

/** 清單列第二格的日期。沒排時間就明講，不要留白讓人以為是資料沒載到 */
function rangeText(t) {
  if (!t.startYmd && !t.dueYmd) return "尚未排定時間";
  if (t.startYmd && t.dueYmd && t.startYmd !== t.dueYmd) {
    return `${shortDate(t.startYmd)} – ${shortDate(t.dueYmd)}`;
  }
  return t.dueYmd ? `${shortDate(t.dueYmd)} 到期` : `${shortDate(t.startYmd)} 開始`;
}

/** 期限一律搶下 attention，其餘才顯示狀態（規則同 pages/MyTask/Index.js 的 tagOf） */
function tagOf(t, todayYmd) {
  if (t.statusKey !== "done" && t.statusKey !== "closed" && t.dueYmd) {
    const d = ymdDiff(todayYmd, t.dueYmd);
    if (d === 0) return { kind: "attention", mark: <MdWarningAmber />, text: "今日到期" };
    if (d !== null && d < 0) return { kind: "attention", mark: <MdWarningAmber />, text: "已逾期" };
  }
  const s = STATUS_TAG[t.statusKey] || STATUS_TAG.pending;
  return { kind: s.kind, mark: s.mark, text: (STATUS[t.statusKey] || STATUS.pending).label };
}

/* ===================== 共用零件 ===================== */

/** 清單列。欄位順序與徽章規則照 pages/MyTask/Index.js 的 ElRow */
function ElRow({ tone, title, metas, tag, onClick }) {
  return (
    <RowButton
      tone={tone === "attention" || tone === "warn" ? tone : ""}
      onClick={onClick}
      title={title}
      meta={metas.length > 0 ? metas.map((m, i) => <span key={`${i}-${m}`}>{m}</span>) : null}
      side={
        <StatusChip kind={tag.kind} mark={tag.mark}>
          {tag.text}
        </StatusChip>
      }
    />
  );
}

ElRow.propTypes = {
  tone: PropTypes.string,
  title: PropTypes.string.isRequired,
  metas: PropTypes.array,
  tag: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};
ElRow.defaultProps = { tone: "", metas: [], onClick: () => {} };

/* ===================== 畫面 ===================== */

function ScheduleGoalPlan(props) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { toast } = useApp();
  const { GET_ProjectList, GET_PhaseList, GET_ScheduleWorkItems, GET_ProjectMembers, GET_HolidayList } = props;

  const [projSel, setProjSel] = useState(params.get("projectId") || "");
  const [phaseSel, setPhaseSel] = useState(params.get("phaseId") || "");
  const [ready, setReady] = useState(false);
  /** 每張卡上還沒送出的選擇：{ [taskId]: { phaseId, assignee } } */
  const [draft, setDraft] = useState({});
  /** 正在送出的那一件，擋住第二次點擊 */
  const [busyId, setBusyId] = useState(null);

  const todayYmd = dayjs().format("YYYY-MM-DD");

  useEffect(() => {
    GET_ProjectList({ Page: 0, PageCount: 0 }, null, null);
  }, [GET_ProjectList]);

  const projects = useMemo(() => {
    const arr = Array.isArray(props.projectList) ? props.projectList : _.get(props.projectList, "data_list", []);
    return (Array.isArray(arr) ? arr : []).filter(Boolean).map((p) => normalizeProject(p));
  }, [props.projectList]);

  /* 網址沒帶專案時退回第一個，頁面才不會停在一片空白（同 Goals.js 的做法） */
  useEffect(() => {
    if (!projSel && projects.length) setProjSel(String(projects[0].id));
  }, [projects, projSel]);

  /* 排完一件之後要重抓：那一件會從左欄消失、跑到右欄，底下的算式也跟著變 */
  const reload = useCallback(() => {
    if (!projSel) return;
    /* take 寫出來的理由見底下那一次呼叫 */
    GET_ScheduleWorkItems({ projectId: Number(projSel), take: 500 }, null, null);
    GET_PhaseList(Number(projSel), null, null);
  }, [projSel, GET_ScheduleWorkItems, GET_PhaseList]);

  useEffect(() => {
    if (!projSel) return;
    setReady(false);
    setDraft({});
    GET_PhaseList(Number(projSel), null, null);
    /*
     * take 要寫出來。
     *
     * /Schedule/WorkItems 的預設是 200 筆（ScheduleController，上限 500），而排序是
     * ProjectId → StartDate → DueDate —— 被截掉的正好是起始日最晚的那一批，
     * 也就是最可能決定 lastEndYmd（工項做完預計哪一天）的那幾件。
     * 不帶 take 的話，超過 200 件工項的專案在這一頁會算出綠燈，而排程讀的是全部工項、
     * 算出紅燈寄信 —— 同一個里程碑畫面與信完全相反。
     * 500 是後端夾住的上限，再大也沒有用；真的超過 500 件的專案要改成分頁把整份撈完。
     */
    GET_ScheduleWorkItems({ projectId: Number(projSel), take: 500 }, null, (v) => {
      if (!v) setReady(true);
    });
    /* 「誰做」的下拉要列真名，所以要專案成員；拿不到就只留原本的指派人 */
    GET_ProjectMembers({ ProjectId: Number(projSel) }, null, null);
  }, [projSel, GET_PhaseList, GET_ScheduleWorkItems, GET_ProjectMembers]);

  /* ---------- 正規化 ---------- */

  const goalsRaw = useMemo(() => {
    const arr = Array.isArray(props.phaseList) ? props.phaseList : _.get(props.phaseList, "data_list", []);
    return (Array.isArray(arr) ? arr : [])
      .filter(Boolean)
      .filter((p) => String(_.get(p, "project_id", projSel)) === String(projSel))
      .map((p) => ({
        id: _.get(p, "id"),
        name: String(_.get(p, "name") || "未命名的里程碑"),
        desc: String(_.get(p, "description") || ""),
        endYmd: ymdOf(_.get(p, "end_date")),
        createdYmd: ymdOf(_.get(p, "create_at")),
        /* 誰訂的。/Phase/List 現在回 create_id 與換好的 create_name（換不到姓名是空字串），
           規則與 Goals.js／GoalNew.js 的 personLabel 同一份：姓名優先，沒有就退回帳號，
           兩個都沒有才整段不渲染 —— 不編一個名字上去 */
        createdBy: String(_.get(p, "create_name") || "").trim() || String(_.get(p, "create_id") || "").trim(),
        statusCode: Number(_.get(p, "status") || 0),
        sortOrder: Number(_.get(p, "sort_order") || 0),
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [props.phaseList, projSel]);

  /*
   * 工項走 /Schedule/WorkItems：它是唯一同時回 phase_id 與起訖日的投影，
   * 也就是唯一算得出「哪幾件還沒排進任何里程碑」的那一支（見 Goals.js 的說明）。
   * raw 整包留著 —— 送出時 PUT /Task 是整筆覆蓋，缺欄位會被清掉。
   */
  const tasksRaw = useMemo(() => {
    const arr = Array.isArray(props.workItems) ? props.workItems : _.get(props.workItems, "data_list", []);
    return (Array.isArray(arr) ? arr : [])
      .filter(Boolean)
      /* 換專案時重抓還沒回來，先擋掉上一個專案殘留的工項 */
      .filter((t) => String(_.get(t, "project_id")) === String(projSel))
      .map((t) => ({
        id: _.get(t, "id"),
        title: String(_.get(t, "title") || "未命名工項"),
        assignee: String(_.get(t, "assignee_name") || ""),
        assigneeAccount: String(_.get(t, "assignee") || ""),
        startYmd: ymdOf(_.get(t, "start_date")),
        dueYmd: ymdOf(_.get(t, "due_date")),
        statusKey: statusKey(_.get(t, "status")),
        phaseId: _.get(t, "phase_id"),
        est: _.get(t, "estimated_hours") == null ? null : Number(_.get(t, "estimated_hours")),
        raw: t,
      }));
  }, [props.workItems, projSel]);

  /* ---------- 假日（算「里程碑前還有幾個工作天」用） ---------- */

  const [holidayMap, setHolidayMap] = useState({});
  const askedYears = useRef([]);
  const [yearTick, setYearTick] = useState(0);

  const goalEndYmd = useMemo(() => {
    const g = goalsRaw.find((x) => String(x.id) === String(phaseSel));
    return g ? g.endYmd : "";
  }, [goalsRaw, phaseSel]);

  useEffect(() => {
    /* 一次只問一年（saga 是 takeLatest，兩年一起送前一次會被取消），
       回來之後再看還缺哪一年。查不到也沒關係，至少會扣掉週末 */
    const years = _.uniq(
      [Number(todayYmd.slice(0, 4)), goalEndYmd ? Number(goalEndYmd.slice(0, 4)) : null].filter(Boolean)
    );
    const next = years.find((y) => !askedYears.current.includes(y));
    if (next == null) return;
    askedYears.current.push(next);
    GET_HolidayList({ year: next }, () => setYearTick((t) => t + 1), null);
  }, [todayYmd, goalEndYmd, yearTick, GET_HolidayList]);

  useEffect(() => {
    const list = Array.isArray(props.holidayList) ? props.holidayList : _.get(props.holidayList, "data_list", []);
    if (!Array.isArray(list) || !list.length) return;
    /* reducer 只留最後一次查詢的那一年，所以在頁面裡自己累積成一張表 */
    setHolidayMap((prev) => {
      let changed = false;
      const next = { ...prev };
      list.forEach((h) => {
        const ymd = ymdOf(_.get(h, "date", _.get(h, "Date", "")));
        if (!ymd) return;
        const name = _.get(h, "name", _.get(h, "Name", "")) || "國定假日";
        if (next[ymd] !== name) {
          next[ymd] = name;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [props.holidayList]);

  /* ---------- 彙總 ---------- */

  /*
   * 黃燈的門檻（「設定 → 偏好設定」的里程碑提醒門檻）。
   * 這一頁與 /Schedule 一定要吃同一個值：門檻改成 5 天、這裡還用 3 天的話，
   * 時程軸說「快貼線」、排工項頁說「還有緩衝」，同一件事兩個答案。
   * 讀不到就退回 rdSummary 的 DEFAULT_BUFFER_DAYS。
   */
  const { bufferDays } = useGoalBufferDays();

  /* 燈號、長條狀態、未排清單全部由 rdSummary 一次算完，與 /Schedule 同一份口徑 */
  const view = useMemo(
    () =>
      buildScheduleView({
        phases: goalsRaw,
        tasks: tasksRaw,
        todayYmd,
        bufferDays,
        holidayMap,
      }),
    [goalsRaw, tasksRaw, todayYmd, bufferDays, holidayMap]
  );

  /* 網址沒指定里程碑時，預設挑最近的一個 —— 那通常就是最該擔心的那一個 */
  useEffect(() => {
    if (phaseSel || !view.goals.length) return;
    const dated = view.goals.filter((g) => g.endYmd && g.level !== "done");
    const pick = dated.length
      ? dated.slice().sort((a, b) => a.endYmd.localeCompare(b.endYmd))[0]
      : view.goals[0];
    setPhaseSel(String(pick.id));
  }, [view.goals, phaseSel]);

  const goal = useMemo(
    () => view.goals.find((g) => String(g.id) === String(phaseSel)) || null,
    [view.goals, phaseSel]
  );

  /* 每件工項在軸上的顏色與那一句話，和 /Schedule 用同一份，兩頁才不會各說各話 */
  const stateById = useMemo(() => {
    const m = new Map();
    view.bars.forEach((b) => m.set(String(b.id), b));
    return m;
  }, [view.bars]);

  /* ---------- 「誰做」的下拉選項 ---------- */

  const memberOptions = useMemo(() => {
    const arr = Array.isArray(props.members) ? props.members : _.get(props.members, "data_list", []);
    const out = [];
    const seen = new Set();
    (Array.isArray(arr) ? arr : []).forEach((m) => {
      const account = String(_.get(m, "account", _.get(m, "Account", "")) || "");
      const name = String(_.get(m, "name", _.get(m, "Name", "")) || account);
      if (!account || seen.has(account)) return;
      seen.add(account);
      out.push({ account, name });
    });
    return out.sort((a, b) => a.name.localeCompare(b.name, "zh-Hant"));
  }, [props.members]);

  /* ---------- 送出 ---------- */

  const assign = async (item) => {
    if (busyId != null) return;
    const d = draft[item.id] || {};
    const targetPhase = d.phaseId !== undefined ? d.phaseId : phaseSel;
    const targetAssignee = d.assignee !== undefined ? d.assignee : item.assigneeAccount;
    if (!targetPhase) {
      toast("請先選擇里程碑", "error");
      return;
    }
    const target = view.goals.find((g) => String(g.id) === String(targetPhase));

    setBusyId(item.id);
    try {
      /*
       * PUT /Task 是整筆覆蓋（見 TaskService.PutTask），表單沒有的欄位一定要帶回原值，
       * 否則說明、進度、標籤、工時會被一次清空。原值全部來自 /Schedule/WorkItems 的投影 ——
       * 它剛好回得出 ReqPutTask 需要的每一個欄位，所以不必為了改一個 PhaseId 再去打一次詳情。
       */
      const r = item.raw || {};
      const payload = {
        TaskId: Number(item.id),
        PhaseId: Number(targetPhase),
        ParentTaskId: r.parent_task_id ?? null,
        Title: r.title,
        Description: r.description || "",
        Status: Number(r.status) || 0,
        Priority: Number(r.priority) || 0,
        /* 空字串會被後端寫成空指派人；沒選就送 null，語意才是「還沒指派」 */
        Assignee: targetAssignee || null,
        /* 日期一律先用當地時區切成 YYYY-MM-DD 再送。直接把 DateTimeOffset 原樣丟回去，
           時區換算一來一回會讓「哪一天」在邊界上位移一天 */
        StartDate: r.start_date ? fmtInputDate(r.start_date) : null,
        DueDate: r.due_date ? fmtInputDate(r.due_date) : null,
        EstimatedHours: r.estimated_hours ?? null,
        ActualHours: r.actual_hours ?? null,
        Progress: Number(r.progress) || 0,
        Tags: r.tags || null,
      };
      await apiPutForm("/Task", { Data: jwtEncode(payload) });
      toast(`已將「${item.title}」排入「${target ? target.name : "里程碑"}」`);
      setDraft((s) => _.omit(s, [String(item.id)]));
      reload();
    } catch (e) {
      toast(apiErrorMessage(e, "排入失敗"), "error");
    } finally {
      setBusyId(null);
    }
  };

  /* ---------- 「這個里程碑排得下嗎」 ---------- */

  /*
   * 整句算式，不是甘特重疊圖（設計稿 L1857-1862）。
   *
   * 能算的只有總量：
   *   可用 ＝ 從今天到里程碑日期之間的上班日（扣週末與國定假日，來自 /Holiday/List）
   *   需要 ＝ 還沒做完的工項估時總和 ÷ 每天 8 小時
   * 算不出來的是「每個人可用幾天」—— 系統沒有請假、沒有個人產能、也沒有
   * 「這個人已經被別的專案排走多少」。設計稿那句「林怡君可用 14 天、蔡鎮宇可用 9 天」
   * 是範本假資料，照抄就是編一個看起來很精準、實際上沒有來源的數字。
   */
  const capacity = useMemo(() => {
    if (!goal) return null;
    const open = goal.tasks.filter((t) => t.statusKey !== "done" && t.statusKey !== "closed");
    const withEst = open.filter((t) => t.est != null && t.est > 0);
    const estSum = withEst.reduce((a, t) => a + t.est, 0);
    return {
      openCount: open.length,
      missingEst: open.length - withEst.length,
      estSum,
      needDays: estSum > 0 ? Math.ceil(estSum / HOURS_PER_DAY) : null,
      availDays: goal.endYmd ? workdaysBetween(todayYmd, goal.endYmd, holidayMap) : null,
      /* 假日表還沒回來時只扣得掉週末，句子要講清楚，不要讓人以為國定假日已經算進去了 */
      hasHolidays: Object.keys(holidayMap).length > 0,
    };
  }, [goal, todayYmd, holidayMap]);

  const capacityLines = useMemo(() => {
    if (!goal || !capacity) return [];
    const lines = [];
    if (capacity.availDays === null) {
      lines.push("此里程碑尚未設定里程碑日期，無法評估是否來得及。");
    } else {
      lines.push(
        `自今日至 ${longDate(goal.endYmd)}，扣除週末${capacity.hasHolidays ? "與國定假日" : "（國定假日尚未載入，僅扣除週末）"}後共 ${capacity.availDays} 個工作天。`
      );
    }
    if (capacity.needDays === null) {
      lines.push(
        capacity.openCount === 0
          ? "目前沒有未完成的工項，無須再安排。"
          : `${capacity.openCount} 件未完成的工項皆未填寫估時，無法計算所需天數。須填寫估時才能評估是否排得下。`
      );
    } else {
      const diff = capacity.availDays === null ? null : capacity.needDays - capacity.availDays;
      const tail =
        diff === null
          ? "。"
          : diff > 0
          ? `，超出 ${diff} 天。`
          : diff === 0
          ? `，恰好用完。`
          : `，尚有 ${-diff} 天可調度。`;
      lines.push(
        `目前已排入且未完成的 ${capacity.openCount - capacity.missingEst} 件工項估時合計 ${capacity.estSum} 小時，以每日 ${HOURS_PER_DAY} 小時換算需 ${capacity.needDays} 個工作天${tail}`
      );
      if (capacity.missingEst > 0) {
        lines.push(`另有 ${capacity.missingEst} 件未填寫估時，未計入上述數字。`);
      }
    }
    /* 誠實的缺口，直接寫在算式底下 —— 不寫的話，看的人會以為這個數字已經考慮過誰有空 */
    lines.push(
      "此處僅計算總量。系統沒有個人可用天數的資料（請假、其他專案的工作），因此無法評估個別人員能否完成。"
    );
    return lines;
  }, [goal, capacity]);

  /* ---------- 導頁與動作 ---------- */

  const proj = projects.find((p) => String(p.id) === String(projSel));

  /*
   * RD 主管不能改里程碑日期（那是訂定者的決定），只能請對方改。
   * 但系統沒有「請對方改日期」這種通知（是誰訂的現在讀得回來了，能通知他這件事還是沒有）——
   * 所以這顆按鈕做的是一件真的做得到的事：把要講的話複製起來，讓他貼給對方。
   * 做不到的事就不要做成一顆按下去只跳「已送出」的假按鈕。
   */
  const askOwner = async () => {
    if (!goal) return;
    const over = goal.overrunDays > 0 ? `，超出 ${goal.overrunDays} 天` : "";
    const msg = `【${proj ? proj.name : "專案"}】里程碑「${goal.name}」的里程碑日期為 ${longDate(goal.endYmd) || "（尚未設定）"}，目前排定的工項預計 ${longDate(goal.lastEndYmd) || "（尚未排定時間）"} 完成${over}。請評估是否調整里程碑日期。`;
    try {
      await navigator.clipboard.writeText(msg);
      toast("訊息已複製，請貼給里程碑訂定者");
    } catch (e) {
      toast("此瀏覽器不允許網頁複製文字，請直接將上述情況轉告訂定者", "error");
    }
  };

  /* ---------- 頁首橫幅 ---------- */

  /*
   * 只有真的算出風險才渲染（硬規則 2、4）。
   * 沒事也掛一條「目前排得下」的橫幅，三天之後就沒有人會看它了，
   * 真的有事那天也一樣不會看 —— 底下的算式本來就會把「排得下」講出來。
   */
  const banner = useMemo(() => {
    if (!ready || !goal) return null;
    if (goal.overrunDays > 0) {
      const worst = goal.worstTask;
      const body = worst
        ? `所屬工項預計 ${longDate(goal.lastEndYmd)} 完成，最晚的是「${worst.title}」。可將其提前、改派他人，或請里程碑訂定者調整日期。`
        : `所屬工項預計 ${longDate(goal.lastEndYmd)} 完成。可將最晚的幾件提前、改派他人，或請里程碑訂定者調整日期。`;
      return {
        kind: "attention",
        mark: <MdWarningAmber />,
        lead: `${longDate(goal.endYmd)}「${goal.name}」時程不足　`,
        body,
      };
    }
    if (goal.level === "attention" && goal.daysLeft !== null && goal.daysLeft < 0) {
      const left = goal.counts.total - goal.counts.done;
      return {
        kind: "attention",
        mark: <MdWarningAmber />,
        lead: `${longDate(goal.endYmd)}「${goal.name}」已逾期　`,
        body: `已超過里程碑日期 ${-goal.daysLeft} 天，尚有 ${left} 件未完成。請重新安排，或請里程碑訂定者將日期調整為可達成的日期。`,
      };
    }
    if (goal.level === "warn") {
      const wd =
        goal.bufferWorkdays !== null && goal.bufferWorkdays !== goal.bufferLeft
          ? `（扣除週末與國定假日僅 ${goal.bufferWorkdays} 個工作日）`
          : "";
      return {
        kind: "warn",
        mark: <MdErrorOutline />,
        lead: `${longDate(goal.endYmd)}「${goal.name}」緩衝不足　`,
        body: `工項預計 ${longDate(goal.lastEndYmd)} 完成，緩衝僅剩 ${goal.bufferLeft} 天${wd}。再排入新工項將超過里程碑日期。`,
      };
    }
    return null;
  }, [ready, goal]);

  /* ---------- 片段 ---------- */

  const unplanned = view.unplanned;

  const unplannedCard = (item, idx) => {
    const d = draft[item.id] || {};
    const phaseVal = d.phaseId !== undefined ? d.phaseId : phaseSel;
    const assigneeVal = d.assignee !== undefined ? d.assignee : item.assigneeAccount;
    /* 原本的指派人不在專案成員名單裡（離開專案、或成員清單沒權限拿到）時
       仍要留得住他，否則一按「排進去」就把人洗掉了 */
    const extra =
      item.assigneeAccount && !memberOptions.some((m) => m.account === item.assigneeAccount)
        ? [{ account: item.assigneeAccount, name: item.assignee || item.assigneeAccount }]
        : [];
    const options = [...extra, ...memberOptions];
    const busy = busyId === item.id;

    const tag = tagOf(item, todayYmd);

    return (
      <Card component="article" key={item.id} sx={{ p: 2 }}>
        {/*
          設計稿這個位置是型別徽章（L1810）。型別不貼：/Schedule/WorkItems 的投影沒有
          issue_type_key，認不得時 typeKey() 一律退回「工作」—— 整排都標「工作」
          比沒有徽章更誤導。改放狀態／期限徽章（規則同 Today 的 tagOf）：
          這一欄本來就混著已逾期、已完成、還沒開始的工項，不標的話每張卡長得一模一樣，
          會有人把一件已經做完的工項又排一次。
        */}
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mb: 1 }}>
          <StatusChip kind={tag.kind} mark={tag.mark}>
            {tag.text}
          </StatusChip>
        </Stack>
        <Typography sx={{ fontWeight: 600 }}>{item.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {`${item.assignee || "未指派"} · ${item.est != null && item.est > 0 ? `估時 ${item.est} 小時` : "未填寫估時"} · ${rangeText(item)}`}
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            fullWidth
            label="排入里程碑"
            id={`plan-goal-${item.id}`}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            value={phaseVal || ""}
              /* 用 setState 的函式形式讀回上一份 draft，不要吃 render 當下的 d ——
                 連續改兩個下拉時，第二次會把第一次的選擇蓋掉 */
              onChange={(e) =>
                setDraft((s) => ({ ...s, [item.id]: { ...(s[item.id] || {}), phaseId: e.target.value } }))
              }
            >
              <option value="">尚未決定</option>
              {view.goals.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {`${g.endYmd ? longDate(g.endYmd) : "尚未設定日期"} · ${g.name}`}
                </option>
              ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="負責人"
            id={`plan-who-${item.id}`}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            value={assigneeVal || ""}
              onChange={(e) =>
                setDraft((s) => ({ ...s, [item.id]: { ...(s[item.id] || {}), assignee: e.target.value } }))
              }
            >
              <option value="">未指派</option>
              {options.map((m) => (
                <option key={m.account} value={m.account}>
                  {m.name}
                </option>
              ))}
          </TextField>
          {/*
            整頁唯一那顆實心主要按鈕是第一張卡上的這一顆（硬規則 1）。
            設計稿 L1817/L1830 也是這樣處理一整排同樣的動作：第一件標成主要動作，
            其餘維持外框按鈕，否則一排全是實心藍按鈕，等於一顆都沒有被強調。
          */}
          <Button
            variant={idx === 0 ? "contained" : "outlined"}
            disabled={busy}
            loading={busy}
            loadingPosition="start"
            onClick={() => assign(item)}
          >
            {busy ? "排入中…" : "排入"}
          </Button>
        </Stack>
      </Card>
    );
  };

  const plannedRow = (t) => {
    const st = stateById.get(String(t.id));
    const tag = st ? { kind: LEVEL_TAG[st.level] || LEVEL_TAG.plan, mark: st.mark, text: st.text } : tagOf(t, todayYmd);
    const tone = st && (st.level === "attention" || st.level === "warn") ? st.level : "";
    return (
      <ElRow
        key={t.id}
        tone={tone}
        title={t.title}
        metas={[
          t.assignee || "未指派",
          rangeText(t),
          t.est != null && t.est > 0 ? `估時 ${t.est} 小時` : "",
        ].filter(Boolean)}
        tag={tag}
        onClick={() => {
          if (t.id != null) navigate(`/Task/${t.id}`);
        }}
      />
    );
  };

  return (
    <>
      <Typography variant="h1" component="h1">
        工項排程
      </Typography>
      <Typography sx={{ mt: 1.5 }}>
        {proj
          ? `${proj.name} · 於此指定各工項所屬的里程碑與負責人。里程碑日期由里程碑訂定者調整，本頁無法變更。`
          : "於此指定各工項所屬的里程碑與負責人。里程碑日期由里程碑訂定者調整，本頁無法變更。"}
      </Typography>

      {/* ---------- 篩選列 ---------- */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
        <Typography component="label" variant="body2" color="text.secondary" htmlFor="plan-proj">
          專案：
        </Typography>
        <TextField
          select
          id="plan-proj"
          /* 寬度跟著內容走：篩選列上的下拉不該橫跨整頁（設計稿也是這樣寫的） */
          sx={{ width: "auto", maxWidth: "100%" }}
          slotProps={{ select: { native: true } }}
          value={projSel}
          onChange={(e) => {
            setProjSel(e.target.value);
            setPhaseSel("");
          }}
        >
          {projects.length === 0 ? <option value="">載入中…</option> : null}
          {projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </TextField>
        {view.goals.length > 0 ? (
          <>
            <Typography component="label" variant="body2" color="text.secondary" htmlFor="plan-phase">
              里程碑：
            </Typography>
            <TextField
              select
              id="plan-phase"
              sx={{ width: "auto", maxWidth: "100%" }}
              slotProps={{ select: { native: true } }}
              value={phaseSel}
              onChange={(e) => setPhaseSel(e.target.value)}
            >
              {view.goals.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {`${g.endYmd ? longDate(g.endYmd) : "尚未設定日期"} · ${g.name}`}
                </option>
              ))}
            </TextField>
          </>
        ) : null}
      </Stack>

      {/* ---------- 先說結論（設計稿 L1791-1794） ---------- */}
      {banner ? (
        <Banner kind={banner.kind} icon={banner.mark} role="alert" sx={{ mt: 2 }}>
          <strong>{banner.lead}</strong>
          {banner.body}
        </Banner>
      ) : null}

      {/* ---------- 這個專案還沒有里程碑：整塊換成空狀態（硬規則 2） ---------- */}
      {ready && view.goals.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, mt: 3 }}>
          <Typography variant="h2">本專案尚無里程碑</Typography>
          <Typography sx={{ mt: 1, mb: 2 }}>
            {"工項須排入里程碑才算完成排程。請先新增里程碑，再於本頁安排工項。"}
          </Typography>
          <Button
            variant="text"
            startIcon={<MdAdd aria-hidden="true" />}
            onClick={() => navigate(`/Schedule/Goal/New?projectId=${encodeURIComponent(projSel)}`)}
          >
            新增里程碑
          </Button>
        </Box>
      ) : null}

      {/* 里程碑與工項還沒回來：先畫出清單輪廓，回來後換成下面的兩欄 */}
      {!ready ? (
        <Card sx={{ mt: 3 }}>
          <SkeletonRows rows={4} label="工項排程載入中" />
        </Card>
      ) : null}

      {/* ---------- 兩欄（設計稿 L1796-1868） ---------- */}
      {ready && view.goals.length > 0 ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mt: 3 }}>
          {/* ===== 左：還沒排的工項 ===== */}
          <Card component="section">
            {/* 列要貼齊卡緣，所以卡片本身不給內距，標頭自己補 */}
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <Typography variant="h2" component="h2">
                未排入里程碑的工項
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {unplanned.length > 0 ? `${unplanned.length} 件` : "0 件"}
              </Typography>
            </Box>
            {unplanned.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
                <Typography variant="h2">所有工項皆已排入里程碑</Typography>
                <Typography sx={{ mt: 1 }}>{"新建立且未指定里程碑的工項將顯示於此處。"}</Typography>
              </Box>
            ) : (
              <Box sx={{ px: 3, pb: 3 }}>
                <Stack spacing={2}>{unplanned.slice(0, UNPLANNED_MAX).map(unplannedCard)}</Stack>
                {unplanned.length > UNPLANNED_MAX ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {`目前列出前 ${UNPLANNED_MAX} 件，排入後將自動補上下一批（尚有 ${unplanned.length - UNPLANNED_MAX} 件）。`}
                  </Typography>
                ) : null}
              </Box>
            )}
          </Card>

          {/* ===== 右：選定的里程碑 ===== */}
          <Card component="section">
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <Typography variant="h2" component="h2">
                {goal ? `${goal.endYmd ? longDate(goal.endYmd) : "未設定里程碑日期"} · ${goal.name}` : "請選擇里程碑"}
              </Typography>
              {/*
                設計稿這裡有一行「林總（總經理）訂的 · 9/5 訂下」（L1838）。
                /Phase/List 現在會回 create_id 與 create_name，所以「誰訂的」寫得出來了
                （職稱還是沒有，不補）。換不到人的那一筆只留「{日期}訂下」這半句，
                不要為了湊格式編一個人名上去。
              */}
              {goal && (goal.createdBy || goal.createdYmd) ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {[goal.createdBy ? `建立者：${goal.createdBy}` : "", goal.createdYmd ? `建立於 ${longDate(goal.createdYmd)}` : ""]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              ) : null}
              {goal ? (
                <Typography variant="body2" color="text.secondary">
                  {goal.counts.total === 0
                    ? "尚無排入的工項"
                    : `${goal.counts.total} 件${goal.lastEndYmd ? ` · 預計 ${longDate(goal.lastEndYmd)} 完成` : ""}`}
                </Typography>
              ) : null}
              {goal && goal.desc ? <Typography sx={{ mt: 1.5 }}>{`達成標準：${goal.desc}`}</Typography> : null}
            </Box>

            {goal && goal.tasks.length === 0 ? (
              /* 空的里程碑不要畫成一塊空白（硬規則 2）：說清楚它現在算不出風險，
                 並把人導回左欄那份還沒排的清單 */
              <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
                <Typography variant="h2">此里程碑尚無工項</Typography>
                <Typography sx={{ mt: 1 }}>
                  {"沒有工項時無法評估是否來得及，此里程碑目前不會出現在任何風險提醒中。請從左側選擇工項排入。"}
                </Typography>
              </Box>
            ) : null}

            {goal ? goal.tasks.map(plannedRow) : null}

            {/* ---------- 這個里程碑排得下嗎（設計稿 L1855-1866） ---------- */}
            {goal ? (
              /* 卡底的結論區：上緣一條分隔線與清單隔開 */
              <Box sx={{ px: 3, py: 2.5, borderTop: 1, borderColor: "divider" }}>
                <Typography>
                  <strong>時程評估</strong>
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {capacityLines.map((line, i) => (
                    <Typography key={`cap-${i}`}>{line}</Typography>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
                  {/* 有最晚那一件才畫這顆：沒有超過里程碑日期時，「換人做」不是一個要做的決定 */}
                  {goal.overrunDays > 0 && goal.worstTask ? (
                    <Button
                      variant="outlined"
                      /* 按鈕是 white-space:nowrap，工項名稱太長會把按鈕撐出畫面
                         （大字模式下更明顯），所以標題先截短；完整名稱放在 title 上 */
                      title={`改派「${goal.worstTask.title}」`}
                      onClick={() => navigate(`/Task/${goal.worstTask.id}`)}
                    >
                      {`改派「${
                        goal.worstTask.title.length > 12
                          ? `${goal.worstTask.title.slice(0, 12)}…`
                          : goal.worstTask.title
                      }」`}
                    </Button>
                  ) : null}
                  {/* 按鈕上寫通稱就好：是誰訂的已經寫在上面那一行了，而按鈕是
                      white-space:nowrap，把名字放進標題會被長名字撐出畫面 */}
                  <Button variant="outlined" onClick={askOwner}>
                    請訂定者調整里程碑日期
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {"點選後將複製通知訊息，供您貼給對方；系統不會自動通知訂定者。"}
                </Typography>
              </Box>
            ) : null}
          </Card>
        </Box>
      ) : null}
    </>
  );
}

/*
 * 資料來源（零新端點）
 *   · GET_ProjectList          → 專案下拉
 *   · GET_PhaseList(projectId) → state.Phase.List，里程碑下拉與右欄
 *   · GET_ScheduleWorkItems    → state.Schedule.WorkItems，未排／已排工項（唯一同時回 phase_id 與起訖日的投影）
 *   · GET_ProjectMembers       → state.Project.Members，「誰做」下拉的真名與帳號
 *   · GET_HolidayList(year)    → state.Holiday.List，可用工作天的換算
 *   · PUT /Task（apiPutForm）   → 寫入 PhaseId 與 Assignee，整筆覆蓋所以要帶回原值
 *
 * 誠實的缺口（畫面上一律不編造）：
 *   1. 個人可用天數 —— 沒有請假、沒有個人產能、也沒有跨專案負載，所以算式只寫總量。
 *   2. 通知訂定者   —— 是誰訂的已經寫得出來（/Phase/List 回 create_id / create_name），
 *      但沒有「請他改日期」這種通知，按鈕只能把話複製起來讓人自己貼過去。
 *      職稱（設計稿的「林總（總經理）」）一樣沒有這份資料，不補。
 *   3. 工項型別     —— /Schedule/WorkItems 沒有 issue_type_key，卡上不貼型別徽章。
 */
const mapStateToProps = (state) => ({
  projectList: _.get(state, "Project.List", []),
  phaseList: _.get(state, "Phase.List", []),
  workItems: _.get(state, "Schedule.WorkItems", []),
  members: _.get(state, "Project.Members", []),
  holidayList: _.get(state, "Holiday.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ProjectList: (payload, callback, loading) => dispatch({ type: "GET_ProjectList", payload, callback, loading }),
  GET_PhaseList: (projectId, callback, loading) => dispatch({ type: "GET_PhaseList", projectId, callback, loading }),
  GET_ScheduleWorkItems: (payload, callback, loading) =>
    dispatch({ type: "GET_ScheduleWorkItems", payload, callback, loading }),
  GET_ProjectMembers: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectMembers", payload, callback, loading }),
  GET_HolidayList: (payload, callback, loading) => dispatch({ type: "GET_HolidayList", payload, callback, loading }),
});

ScheduleGoalPlan.propTypes = {
  projectList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  phaseList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  workItems: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  members: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  holidayList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_ProjectList: PropTypes.func,
  GET_PhaseList: PropTypes.func,
  GET_ScheduleWorkItems: PropTypes.func,
  GET_ProjectMembers: PropTypes.func,
  GET_HolidayList: PropTypes.func,
};

ScheduleGoalPlan.defaultProps = {
  projectList: [],
  phaseList: [],
  workItems: [],
  members: [],
  holidayList: [],
  GET_ProjectList: () => {},
  GET_PhaseList: () => {},
  GET_ScheduleWorkItems: () => {},
  GET_ProjectMembers: () => {},
  GET_HolidayList: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleGoalPlan);
